import { useMemo } from 'react';
import { useAuthContext } from 'contexts/AuthContext';
import { useOrganizationAccess } from 'hooks/useOrganizationAccess';
import { useGetOrganizationAddons } from 'hooks/useGetOrganizationAddons';
import { useTypedFlags } from 'hooks/useTypedFlags';
import { isTurkUserClass } from 'constants/auth';
import { PORTFOLIO_ANALYTICS_PRO_ADDON_ID } from 'components/NSNavbar/constants';
import { FEATURE_IDS, USER_CLASSES } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { SIDE_NAV_TEST_IDS } from '../../constants';
import SideNavSection from '../SideNavSection/SideNavSection';
import { buildPortfolioSections } from './buildPortfolioSections';

interface IPortfolioSideNav {
    isCollapsed: boolean;
    onCollapsedParentClick: (parentId: string) => void;
    openParentIds: Set<string>;
    onOpenParent: (parentId: string) => void;
    onToggleParent: (parentId: string) => void;
}

const PortfolioSideNav = ({
    isCollapsed,
    onCollapsedParentClick,
    openParentIds,
    onOpenParent,
    onToggleParent,
}: IPortfolioSideNav) => {
    const { selectedOrganizationId, isFeatureAccessibleToUser, user } = useAuthContext();
    const { hasCoreAccess, isLoading } = useOrganizationAccess();
    const { data: organizationAddons } = useGetOrganizationAddons(selectedOrganizationId!);
    const { ldFlagVendorRevampQ22026 } = useTypedFlags();

    const isPAPEnabled = Boolean(
        organizationAddons?.some(addon => addon.id === PORTFOLIO_ANALYTICS_PRO_ADDON_ID),
    ) || user?.userClass === USER_CLASSES.SUPER_ADMIN;
    const hasContactsAccess = isFeatureAccessibleToUser([FEATURE_IDS.CRM_CONTACTS], AccessLevels.READ_ACCESS);
    const hasCompaniesAccess = isFeatureAccessibleToUser([FEATURE_IDS.CRM_COMPANIES], AccessLevels.READ_ACCESS);

    const isTurkUser = isTurkUserClass(user?.userClass);

    const sections = useMemo(() => buildPortfolioSections({
        hasCoreAppAccess: Boolean(hasCoreAccess),
        isPAPEnabled,
        ldFlagVendorRevampQ22026: Boolean(ldFlagVendorRevampQ22026),
        hasContactsAccess,
        hasCompaniesAccess,
        isTurkUser,
    }), [hasCoreAccess, isPAPEnabled, ldFlagVendorRevampQ22026, hasContactsAccess, hasCompaniesAccess, isTurkUser]);

    if (isLoading) {
        return (
            <div
                className="NSNavBarV2__loading"
                data-testid={SIDE_NAV_TEST_IDS.LOADING}
                aria-hidden="true"
            />
        );
    }

    return (
        <div data-testid={SIDE_NAV_TEST_IDS.PORTFOLIO}>
            {sections.map(section => (
                <SideNavSection
                    key={section.id}
                    section={section}
                    isCollapsed={isCollapsed}
                    onCollapsedParentClick={onCollapsedParentClick}
                    openParentIds={openParentIds}
                    onOpenParent={onOpenParent}
                    onToggleParent={onToggleParent}
                />
            ))}
        </div>
    );
};

export default PortfolioSideNav;
